import React, { Fragment, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { format } from "date-fns";
import { Switch, Transition } from "@headlessui/react";

import useTheme from "hooks/useTheme";

import { deleteUserProfileFromUsers, getUserProfile, updateProfileInfoByID } from "actions/profile";
import { updateUserPreferencesByID } from "actions/userPreferences";
import { convertBase64 } from "helpers/functions";

import { BsShopWindow } from "react-icons/bs";
import { CgOptions, CgNotes } from "react-icons/cg";
import { RiFileChartLine, RiLockPasswordLine } from "react-icons/ri";
import { GiCash } from "react-icons/gi";
import { TbFaceId } from "react-icons/tb";
import { FaUser } from "react-icons/fa";
import { BiWorld } from "react-icons/bi";
import { HiOutlineFingerPrint, HiOutlineMail } from "react-icons/hi";
import { ImPhone } from "react-icons/im";

import DeleteModal from "theme/modals/partials/DeleteByPinModal";

import ProfileIcon from "theme/icons/Profile";
import DeviceIcon from "theme/icons/Device";
import LockIcon from "theme/icons/Lock";
import DaffiButtonIcon from "theme/icons/DaffiButton";
import EditIcon from "theme/icons/Edit";
import GarbageIcon from "theme/icons/Garbage";
import useNotification from "hooks/useNotification";
import ReportsIcon from "theme/icons/Reports";
import ComingSoonModal from "theme/modals/partials/ComingSoonModal";

const Profile = ({ navigatePage }) => {
	const dispatch = useDispatch();
	const { appLanguage, themeColor } = useTheme();
	const { notify } = useNotification();

	const profile = useSelector((state) => state.Profile);
	const userPreferences = useSelector((state) => state.UserPreferences);
	const accounts = useSelector((state) => state.Accounts);

	const [tempProfileInfo, setTempProfileInfo] = useState(null);
	const [tempProfilePreferences, setTempProfilePreferences] = useState(null);
	const [mainAccount, setMainAccount] = useState(null);

	const [comingSoonModalIsOpen, setComingSoonModalIsOpen] = useState(false);
	const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false);

	const [isDataUpdated, setIsDataUpdated] = useState(false);
	const [isPreferencesUpdated, setIsPreferencesUpdated] = useState(false);

	const hiddenImageFileInput = useRef();

	useEffect(() => {
		dispatch(getUserProfile());
		// dispatch(getUserByToken());
		// dispatch(getUserPreferencesByUsername());

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (accounts?.length) setMainAccount(accounts[0]);
	}, [accounts]);

	useEffect(() => {
		setTempProfileInfo(profile);
		setDeleteModalIsOpen(false);
	}, [profile]);

	useEffect(() => {
		setTempProfilePreferences(userPreferences?.preferences);
	}, [userPreferences]);

	useEffect(() => {
		if (isDataUpdated) handleSubmit();

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isDataUpdated]);

	const handleFileRead = async (e) => {
		const file = e.target.files[0];
		const base64 = await convertBase64(file);

		setTempProfileInfo({ ...tempProfileInfo, "profile-image": base64 });

		setIsDataUpdated(true);
	};

	const handleChange = (e) => {
		setTempProfileInfo({
			...tempProfileInfo,
			[e.target.name]: e.target.valueAsNumber || e.target.value,
		});

		setIsDataUpdated(true);
	};

	const handleSubmit = () => {
		if (isDataUpdated) {
			dispatch(updateProfileInfoByID(tempProfileInfo, notify));
			setIsDataUpdated(false);
		}

		// if (isPreferencesUpdated)
		// 	dispatch(
		// 		updateUserPreferencesByID({
		// 			preferences: tempProfilePreferences,
		// 			_id: userPreferences?._id,
		// 		})
		// 	);

		// setIsPreferencesUpdated(false);
	};

	const EnableFingerprint = (e) => {
		setTempProfilePreferences({ ...tempProfilePreferences, "enable-fingerprint": e });

		setIsPreferencesUpdated(true);
	};

	const EnableFaceID = (e) => {
		setTempProfilePreferences({ ...tempProfilePreferences, "enable-face-id": e });

		setIsPreferencesUpdated(true);
	};

	const EnablePin = (e) => {
		setTempProfilePreferences({ ...tempProfilePreferences, "enable-pin": e });

		setIsPreferencesUpdated(true);
	};

	const EnableKeepUnlock = (e) => {
		setTempProfilePreferences({ ...tempProfilePreferences, "keep-unlock": e });

		setIsPreferencesUpdated(true);
	};

	return (
		tempProfileInfo && (
			<Transition
				as={Fragment}
				appear={true}
				show={true}
				enter="transition-opacity duration-500"
				enterFrom="opacity-0"
				enterTo="opacity-100"
			>
				<div className="items-center justify-start w-full flex-col px-5 space-y-2">
					<div className="items-start justify-start w-full flex">
						{/* User Profile Area */}
						<div className="items-start justify-start w-[50%] flex-col pr-4 space-y-4">
							<div className={`flex items-center w-full space-x-4`}>
								<h1 className={`w-fit text-xl font-bold ${themeColor?.text} pb-1`}>{appLanguage?.myProfile}</h1>
								{/* {isDataUpdated ? (
									<button
										className={`text-md bg-candy-red text-white px-3 py-1 rounded-lg`}
										onClick={() => handleSubmit()}
									>
										Save
									</button>
								) : (
									<></>
								)} */}
							</div>
							<div className="flex w-full items-start">
								<div className="flex-col relative">
									{tempProfileInfo?.["profile-image"] ? (
										<img
											className={`w-14 h-14 object-contain object-center`}
											src={tempProfileInfo["profile-image"]}
											alt="temp"
											loading="lazy"
										/>
									) : (
										<ProfileIcon className="w-14 h-14" fill={themeColor?.hex} />
									)}
									<EditIcon
										className="w-5 absolute -bottom-1 -right-1 cursor-pointer bg-white rounded-md hover:shadow-lg"
										fill={themeColor?.hex}
										onClick={() => hiddenImageFileInput.current.click()}
									/>
									<input
										style={{ display: "none" }}
										ref={hiddenImageFileInput}
										type="file"
										accept="image/x-png,image/jpeg"
										onChange={(e) => handleFileRead(e)}
									/>
								</div>
								<div className="flex-col w-full space-y-2 pl-5">
									<div className="flex items-start justify-between">
										<div className="flex-col">
											<h1 className={`text-md font-bold ${themeColor?.text}`}>
												{tempProfileInfo["first-name"]}
											</h1>
											<h1 className={`text-md font-bold ${themeColor?.text}`}>
												{tempProfileInfo["last-name"]}
											</h1>
										</div>
									</div>
									<div className="w-full flex space-x-2 items-center justify-between">
										<div className="flex">
											<ReportsIcon
												className="w-4 cursor-pointer"
												fill={themeColor?.hex}
												onClick={() => setComingSoonModalIsOpen(true)}
											/>
										</div>
									</div>
								</div>
							</div>

							<div className="flex-col w-full space-y-3">
								<div className="flex w-full space-x-3 items-center">
									<FaUser className={`text-2xl text-bold ${themeColor?.text}`} />

									<div className="flex w-full space-x-2">
										<input
											className={`italic text-xs rounded-3xl px-3 py-2 border focus:outline-none`}
											placeholder={appLanguage?.firstName}
											name="first-name"
											value={tempProfileInfo["first-name"] ? tempProfileInfo["first-name"] : ""}
											onChange={(e) => handleChange(e)}
										/>
										<input
											className={`italic text-xs rounded-3xl px-3 py-2 border focus:outline-none`}
											placeholder={appLanguage?.lastName}
											name="last-name"
											value={tempProfileInfo["last-name"] ? tempProfileInfo["last-name"] : ""}
											onChange={(e) => handleChange(e)}
										/>
									</div>
								</div>

								<div className="flex w-full space-x-3 items-center">
									<GiCash className={`text-2xl text-bold text-white`} />
									<input
										className={`w-full italic text-xs rounded-3xl px-3 py-2 border ${themeColor?.text}`}
										placeholder={appLanguage?.username}
										name="username"
										value={tempProfileInfo?.username ? tempProfileInfo.username : ""}
										disabled
										onChange={(e) => handleChange(e)}
									/>
								</div>

								<div className="flex w-full space-x-3 items-center">
									<GiCash className={`text-2xl text-bold ${themeColor?.text}`} />
									<div className="flex w-full items-center justify-between space-x-1">
										<input
											className={`italic text-xs rounded-3xl px-3 py-2 border ${themeColor?.text}`}
											placeholder={appLanguage?.accountAddress}
											name="account-address"
											value={
												mainAccount && mainAccount["account-address"]
													? `${mainAccount["account-address"].slice(0, 5)}...${mainAccount[
															"account-address"
													  ].slice(-5)}`
													: ""
											}
											disabled
											onChange={(e) => handleChange(e)}
										/>
										<input
											className={`italic text-xs rounded-3xl px-3 py-2 border ${themeColor?.text}`}
											placeholder={appLanguage?.nfdFriendlyName}
											name="account-address"
											value={mainAccount?.name ? mainAccount.name : ""}
											disabled
											onChange={(e) => handleChange(e)}
										/>
									</div>
								</div>
								<div className="flex w-full space-x-3 items-center">
									<BsShopWindow className={`text-2xl text-bold ${themeColor?.text}`} />
									<input
										className={`w-full italic text-xs border rounded-3xl px-3 py-2 focus:outline-none`}
										placeholder={appLanguage?.company}
										name="company"
										value={tempProfileInfo?.company ? tempProfileInfo.company : ""}
										onChange={(e) => handleChange(e)}
									/>
								</div>
								<div className="flex w-full space-x-3 items-center">
									<ImPhone className={`text-2xl text-bold ${themeColor?.text}`} />
									<input
										className={`w-full italic text-xs border rounded-3xl px-3 py-2 focus:outline-none`}
										placeholder={appLanguage?.phoneNumber}
										name="phone"
										value={tempProfileInfo?.phone ? tempProfileInfo.phone : ""}
										onChange={(e) => handleChange(e)}
									/>
								</div>
								<div className="flex w-full space-x-3 items-center">
									<HiOutlineMail className={`text-2xl text-bold ${themeColor?.text}`} />
									<input
										className={`w-full italic text-xs border rounded-3xl px-3 py-2 focus:outline-none`}
										placeholder={appLanguage?.email}
										name="email"
										value={tempProfileInfo?.email ? tempProfileInfo.email : ""}
										onChange={(e) => handleChange(e)}
									/>
								</div>
								<div className="flex w-full space-x-3 items-start">
									<BiWorld className={`text-2xl text-bold ${themeColor?.text}`} />
									<textarea
										rows="4"
										className={`w-full italic text-xs border rounded-3xl px-3 py-2 focus:outline-none`}
										placeholder={appLanguage?.streetAddress}
										name="street-address"
										value={tempProfileInfo["street-address"] ? tempProfileInfo["street-address"] : ""}
										onChange={(e) => handleChange(e)}
									></textarea>
								</div>
								<div className="flex w-full space-x-3 items-start">
									<CgNotes className={`text-2xl text-bold ${themeColor?.text}`} />
									<textarea
										rows="2"
										className={`w-full italic text-xs border rounded-3xl px-3 py-2 focus:outline-none`}
										placeholder={appLanguage?.notes}
										name="notes"
										value={tempProfileInfo?.notes ? tempProfileInfo.notes : ""}
										onChange={(e) => handleChange(e)}
									></textarea>
								</div>
							</div>
							<div className="flex-col w-full space-y-2">
								<div className="flex w-full space-x-20 items-center justify-between">
									<p className={`text-xs ${themeColor?.text}`}>{appLanguage?.creationDate}</p>
									<p className={`text-xs ${themeColor?.text}`}>
										{tempProfileInfo?.["created-at"]
											? format(tempProfileInfo["created-at"], "MMM dd, yyyy")
											: "-"}
									</p>
								</div>
								<div className="flex w-full space-x-20 items-center justify-between">
									<p className={`text-xs ${themeColor?.text}`}>{appLanguage?.lastUsedDate}</p>
									<p className={`text-xs ${themeColor?.text}`}>
										{tempProfileInfo?.["updated-at"]
											? format(tempProfileInfo["updated-at"], "MMM dd, yyyy")
											: "-"}
									</p>
								</div>
							</div>
						</div>

						<div className="items-start justify-start w-[50%] flex-col pl-4 space-y-10">
							<div className={`flex items-center w-full space-x-4`}>
								{/* <h1 className={`w-fit text-xl font-bold ${themeColor?.text} pb-1`}>
									Profile Preferences
								</h1> 
								 {isPreferencesUpdated ? (
									<button
										className={`text-md bg-candy-red text-white px-3 py-1 rounded-lg`}
										onClick={() => handleSubmit()}
									>
										Save
									</button>
								) : (
									<></>
								)} */}
							</div>

							<div className="flex-col w-full space-y-7">
								<div className="flex w-full items-center justify-between">
									<div className="flex space-x-3 items-center">
										{/* <HiOutlineFingerPrint className={`text-2xl text-bold ${themeColor?.text}`} />
										<p className={`text-sm ${themeColor?.text}`}>Enable Fingerprint ID</p> */}
										<HiOutlineFingerPrint className={`text-2xl text-bold ${themeColor?.text} opacity-20`} />
										<p className={`text-sm ${themeColor?.text} opacity-20`}>
											{appLanguage?.enableFingerPrintID}
										</p>
									</div>
									<div className="flex space-x-3">
										<Switch
											defaultChecked={tempProfilePreferences?.["enable-fingerprint"]}
											// onChange={(e) => EnableFingerprint(e)}
											as={Fragment}
										>
											{({ checked }) => (
												<button
													className={`${
														checked ? themeColor?.bg : `${themeColor?.bg} bg-opacity-20`
													} relative inline-flex h-5 w-10 shrink-0 rounded-full smooth border-2 border-transparent`}
													disabled
												>
													<span
														className={`${
															checked ? "translate-x-5" : "translate-x-0"
														} h-4 w-4 rounded-full bg-white shadow-xl smooth`}
													/>
												</button>
											)}
										</Switch>

										{/* <CgOptions className={`text-2xl text-bold ${themeColor?.text}`} /> */}
										<CgOptions className={`text-2xl text-bold ${themeColor?.text} opacity-20`} />
									</div>
								</div>

								<div className="flex w-full items-center justify-between">
									<div className="flex space-x-3 items-center">
										{/* <TbFaceId className={`text-2xl text-bold ${themeColor?.text}`} />
										<p className={`text-sm ${themeColor?.text}`}>Enable Face ID</p> */}

										<TbFaceId className={`text-2xl text-bold ${themeColor?.text} opacity-20`} />
										<p className={`text-sm ${themeColor?.text} opacity-20`}>{appLanguage?.enableFaceID}</p>
									</div>
									<div className="flex space-x-3">
										<Switch
											defaultChecked={tempProfilePreferences?.["enable-face-id"]}
											// onChange={(e) => EnableFaceID(e)}
											as={Fragment}
										>
											{({ checked }) => (
												<button
													className={`${
														checked ? themeColor?.bg : `${themeColor?.bg} bg-opacity-20`
													} relative inline-flex h-5 w-10 shrink-0 rounded-full smooth border-2 border-transparent`}
													disabled
												>
													<span
														className={`${
															checked ? "translate-x-5" : "translate-x-0"
														} h-4 w-4 rounded-full bg-white shadow-xl smooth`}
													/>
												</button>
											)}
										</Switch>

										{/* <CgOptions className={`text-2xl text-bold ${themeColor?.text}`} /> */}
										<CgOptions className={`text-2xl text-bold ${themeColor?.text} opacity-20`} />
									</div>
								</div>

								<div className="flex w-full items-center justify-between">
									<div className="flex space-x-3 items-center">
										<RiLockPasswordLine className={`text-2xl text-bold ${themeColor?.text} opacity-20`} />
										<p className={`text-sm ${themeColor?.text} opacity-20`}>{appLanguage?.allowPin}</p>
									</div>
									<div className="flex space-x-3">
										<Switch
											defaultChecked={tempProfilePreferences?.["enable-pin"]}
											onChange={(e) => EnablePin(e)}
											as={Fragment}
										>
											{({ checked }) => (
												<button
													className={`${
														checked ? themeColor?.bg : `${themeColor?.bg} bg-opacity-20`
													} relative inline-flex h-5 w-10 shrink-0 rounded-full smooth border-2 border-transparent`}
													disabled
												>
													<span
														className={`${
															checked ? "translate-x-5" : "translate-x-0"
														} h-4 w-4 rounded-full bg-white shadow-xl smooth`}
													/>
												</button>
											)}
										</Switch>

										<CgOptions className={`text-2xl text-bold ${themeColor?.text} opacity-20`} />
									</div>
								</div>

								<div className="flex w-full items-center justify-between">
									<div className="flex space-x-3">
										<HiOutlineFingerPrint className={`text-2xl text-bold ${themeColor?.text} opacity-20`} />
										<p className={`text-sm ${themeColor?.text} opacity-20`}>{appLanguage?.keepUnlock}</p>
									</div>
									<div className="flex space-x-3">
										<Switch
											defaultChecked={tempProfilePreferences?.["keep-unlock"]}
											onChange={(e) => EnableKeepUnlock(e)}
											as={Fragment}
										>
											{({ checked }) => (
												<button
													className={`${
														checked ? themeColor?.bg : `${themeColor?.bg} bg-opacity-20`
													} relative inline-flex h-5 w-10 shrink-0 rounded-full smooth border-2 border-transparent`}
													disabled
												>
													<span
														className={`${
															checked ? "translate-x-5" : "translate-x-0"
														} h-4 w-4 rounded-full bg-white shadow-xl smooth`}
													/>
												</button>
											)}
										</Switch>

										<CgOptions className={`text-2xl text-bold ${themeColor?.text} opacity-20`} />
									</div>
								</div>
							</div>

							<div className="flex-col w-full space-y-7">
								<div className="flex w-full space-x-3 items-center">
									<DaffiButtonIcon className={`w-5 opacity-20`} fill={themeColor?.hex} />
									<div className="flex w-full items-center justify-between space-x-3">
										<p className={`flex w-fit text-sm ${themeColor?.text} opacity-20`}>
											{appLanguage?.daffiUserAccount}
										</p>
										<input
											className={`flex flex-grow w-fit italic text-xs rounded-3xl px-3 py-2 border ${themeColor?.text} opacity-20`}
											placeholder={tempProfileInfo?.email}
											disabled
										/>
									</div>
								</div>

								<div className="flex w-full items-center justify-between">
									<div className="flex space-x-3">
										<LockIcon className={`w-4 opacity-20`} fill={themeColor?.hex} />
										<p className={`text-sm ${themeColor?.text} opacity-20`}>
											{appLanguage?.changeUserAccountPassword}
										</p>
									</div>

									<CgOptions className={`text-2xl text-bold ${themeColor?.text} opacity-20`} />
								</div>

								<div className="flex w-full space-x-3 items-center">
									<DeviceIcon className={`w-4 opacity-20`} fill={themeColor?.hex} />
									<div className="flex w-full items-center justify-between space-x-3">
										<p className={`flex w-fit text-sm ${themeColor?.text} opacity-20`}>
											{appLanguage?.devicesAuthorized}
										</p>
										<select
											className={`flex flex-grow w-fit italic text-xs rounded-3xl px-2 py-2 border ${themeColor?.text} opacity-20`}
											disabled
										>
											<option>-</option>
										</select>
									</div>
								</div>
							</div>
							<div className="flex w-full items-center justify-center">
								<button
									className={`flex flex-col w-fit items-center smooth py-3 px-5 space-y-2 rounded-xl hover:shadow-lg`}
									onClick={() => setDeleteModalIsOpen(true)}
								>
									<GarbageIcon className="w-3" fill="#FC4242" />
									<p
										className={`w-full text-xs font-normal ${themeColor?.text}`}
										dangerouslySetInnerHTML={{ __html: appLanguage?.deleteMyDaffiWalletFromThisDevice }}
									/>
								</button>
							</div>
						</div>
					</div>

					<ComingSoonModal isOpen={comingSoonModalIsOpen} setIsOpen={setComingSoonModalIsOpen} />

					<DeleteModal
						isOpen={deleteModalIsOpen}
						setIsOpen={setDeleteModalIsOpen}
						title={tempProfileInfo?.name}
						action={() => dispatch(deleteUserProfileFromUsers({ _id: tempProfileInfo?._id }, navigatePage, notify))}
					/>
				</div>
			</Transition>
		)
	);
};

export default Profile;
